import { useCallback, useEffect, useState } from 'react';
import { getAttachmentUrl } from '../utils/attachments';

const attachmentUrlCache = new Map();

const loadAttachment = (attachment) => {
  const cached = attachmentUrlCache.get(attachment);
  if (cached) return cached;

  const request = fetch(getAttachmentUrl(attachment), {
    credentials: 'include',
  }).then((response) => {
    if (!response.ok) throw new Error('Unable to load attachment.');
    return response.blob();
  }).then((blob) => {
    const objectUrl = URL.createObjectURL(blob);
    attachmentUrlCache.set(attachment, Promise.resolve(objectUrl));
    return objectUrl;
  }).catch((error) => {
    attachmentUrlCache.delete(attachment);
    throw error;
  });

  attachmentUrlCache.set(attachment, request);
  return request;
};

export const useAuthenticatedAttachmentUrl = (attachment) => {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ attachment: null, status: 'idle', url: '' });

  useEffect(() => {
    if (!attachment) {
      setResult({ attachment: null, status: 'idle', url: '' });
      return undefined;
    }

    let active = true;
    setResult({ attachment, status: 'loading', url: '' });
    loadAttachment(attachment).then((url) => {
      if (active) setResult({ attachment, status: 'ready', url });
    }).catch((error) => {
      if (active) setResult({ attachment, status: 'error', url: '' });
      if (process.env.NODE_ENV !== 'production') console.error(error.message);
    });
    return () => { active = false; };
  }, [attachment, attempt]);

  const retry = useCallback(() => {
    attachmentUrlCache.delete(attachment);
    setAttempt((value) => value + 1);
  }, [attachment]);

  if (result.attachment !== attachment) {
    return { status: attachment ? 'loading' : 'idle', url: '', retry };
  }
  return { status: result.status, url: result.url, retry };
};

export const AuthenticatedAttachmentImage = ({ attachment, alt = '', ...props }) => {
  const { status, url } = useAuthenticatedAttachmentUrl(attachment);

  if (status !== 'ready') {
    return <span className='h-full w-full animate-pulse bg-slate-300/15' aria-hidden='true' />;
  }

  return <img src={url} alt={alt} {...props} />;
};
